/**
 * Platform-specific code of the documentation page. Every snippet is a function of the live
 * widget URL, so what the page shows is always ready to paste with the current configuration.
 *
 * The Android snippets follow the Android sample app (WebView + FusedLocationProvider,
 * evaluateJavascript with a readiness check); iOS and Flutter use the same pattern with
 * WKWebView and webview_flutter.
 */

export type WidgetPlatform = 'android' | 'ios' | 'flutter';

export interface PlatformInfo {
  id: WidgetPlatform;
  label: string;
  language: string;
}

export const PLATFORMS: PlatformInfo[] = [
  { id: 'android', label: 'Android', language: 'Kotlin' },
  { id: 'ios', label: 'iOS', language: 'Swift' },
  { id: 'flutter', label: 'Flutter', language: 'Dart' },
];

export interface Snippet {
  id: string;
  title: string;
  code: string;
}

/** Characters that would end the string literal the URL is pasted into. */
function quoted(url: string, quote: '"' | "'"): string {
  return url.replace(/\\/g, '\\\\').replace(quote === '"' ? /"/g : /'/g, '\\' + quote);
}

// ---------------------------------------------------------------------------
// Setup: dependencies, permissions, the WebView loading the URL
// ---------------------------------------------------------------------------

export function setupSnippets(platform: WidgetPlatform, url: string): Snippet[] {
  switch (platform) {
    case 'android':
      return [
        {
          id: 'android-manifest', title: 'AndroidManifest.xml',
          code:
`<uses-permission android:name="android.permission.INTERNET" />

<!-- Only for your own location source (see Native bridge);
     the widget itself never asks the WebView for geolocation. -->
<uses-permission android:name="android.permission.ACCESS_FINE_LOCATION" />
<uses-permission android:name="android.permission.ACCESS_COARSE_LOCATION" />`,
        },
        {
          id: 'android-activity', title: 'WidgetActivity.kt',
          code:
`class WidgetActivity : ComponentActivity() {

    private lateinit var webView: WebView

    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        webView = WebView(this).apply {
            settings.javaScriptEnabled = true
            settings.domStorageEnabled = true                 // the widget keeps its settings in localStorage
            settings.mediaPlaybackRequiresUserGesture = false // voice guidance
            settings.setGeolocationEnabled(false)             // positions come from the app
            webViewClient = WebViewClient()
        }
        setContentView(webView)
        webView.loadUrl(WIDGET_URL)
    }

    override fun onResume() { super.onResume(); webView.onResume() }
    override fun onPause() { webView.onPause(); super.onPause() }
    override fun onDestroy() { webView.destroy(); super.onDestroy() }

    companion object {
        const val WIDGET_URL = "${quoted(url, '"')}"
    }
}`,
        },
      ];

    case 'ios':
      return [
        {
          id: 'ios-plist', title: 'Info.plist',
          code:
`<!-- Only for your own CoreLocation updates (see Native bridge);
     the widget itself never asks WKWebView for geolocation. -->
<key>NSLocationWhenInUseUsageDescription</key>
<string>Your position is shown on the navigation map.</string>`,
        },
        {
          id: 'ios-controller', title: 'WidgetViewController.swift',
          code:
`import UIKit
import WebKit

final class WidgetViewController: UIViewController {

    private var webView: WKWebView!
    private let widgetURL = URL(string: "${quoted(url, '"')}")!

    override func loadView() {
        let config = WKWebViewConfiguration()
        config.allowsInlineMediaPlayback = true
        config.mediaTypesRequiringUserActionForPlayback = []   // voice guidance
        webView = WKWebView(frame: .zero, configuration: config)
        view = webView
    }

    override func viewDidLoad() {
        super.viewDidLoad()
        webView.load(URLRequest(url: widgetURL))
    }
}`,
        },
      ];

    case 'flutter':
      return [
        {
          id: 'flutter-deps', title: 'Dependencies',
          code:
`flutter pub add webview_flutter webview_flutter_android webview_flutter_wkwebview geolocator

# android/app/src/main/AndroidManifest.xml
#   <uses-permission android:name="android.permission.INTERNET" />
#   <uses-permission android:name="android.permission.ACCESS_FINE_LOCATION" />
# ios/Runner/Info.plist
#   NSLocationWhenInUseUsageDescription`,
        },
        {
          id: 'flutter-page', title: 'widget_page.dart',
          code:
`import 'package:flutter/material.dart';
import 'package:webview_flutter/webview_flutter.dart';
import 'package:webview_flutter_android/webview_flutter_android.dart';
import 'package:webview_flutter_wkwebview/webview_flutter_wkwebview.dart';

const widgetUrl = '${quoted(url, "'")}';

class WidgetPage extends StatefulWidget {
  const WidgetPage({super.key});

  @override
  State<WidgetPage> createState() => _WidgetPageState();
}

class _WidgetPageState extends State<WidgetPage> {
  late final WebViewController controller;

  @override
  void initState() {
    super.initState();
    // Voice guidance must be able to play without a tap on both platforms.
    final params = WebViewPlatform.instance is WebKitWebViewPlatform
        ? WebKitWebViewControllerCreationParams(
            allowsInlineMediaPlayback: true,
            mediaTypesRequiringUserAction: const <PlaybackMediaTypes>{},
          )
        : const PlatformWebViewControllerCreationParams();
    controller = WebViewController.fromPlatformCreationParams(params)
      ..setJavaScriptMode(JavaScriptMode.unrestricted)
      ..loadRequest(Uri.parse(widgetUrl));
    if (controller.platform is AndroidWebViewController) {
      (controller.platform as AndroidWebViewController).setMediaPlaybackRequiresUserGesture(false);
    }
  }

  @override
  Widget build(BuildContext context) =>
      Scaffold(body: SafeArea(child: WebViewWidget(controller: controller)));
}`,
        },
      ];
  }
}

// ---------------------------------------------------------------------------
// Native bridge: pushing GPS positions and the battery level
// ---------------------------------------------------------------------------

export function bridgeSnippets(platform: WidgetPlatform): Snippet[] {
  switch (platform) {
    case 'android':
      return [
        {
          id: 'android-bridge', title: 'Push positions and battery (Kotlin)',
          code:
`/** Returns false through the callback until window.evWidget exists — keep the fix and retry. */
fun pushPosition(location: Location) {
    // Locale.US: a German device would otherwise write 47,4979.
    val js = String.format(
        Locale.US,
        "(function(){ if (!window.evWidget) return false; window.evWidget.gpsPositionUpdate(" +
            "{ lat: %.7f, lng: %.7f, speed: %.2f, heading: %.1f, accuracy: %.1f, altitude: %.1f, timestamp: %d }" +
            "); return true; })()",
        location.latitude, location.longitude, location.speed, location.bearing,
        location.accuracy, location.altitude, location.time,
    )
    webView.evaluateJavascript(js) { result ->
        if (result != "true") { /* widget not ready yet: retry in ~300 ms */ }
    }
}

fun pushBattery(level: Int) {
    webView.evaluateJavascript(
        "(function(){ if (!window.evWidget) return false; window.evWidget.setBattery($level); return true; })()",
        null,
    )
}

// FusedLocationProviderClient, one fix per second while the screen is visible
val request = LocationRequest.Builder(Priority.PRIORITY_HIGH_ACCURACY, 1000L).build()
fusedClient.requestLocationUpdates(request, { location -> pushPosition(location) }, Looper.getMainLooper())`,
        },
      ];

    case 'ios':
      return [
        {
          id: 'ios-bridge', title: 'Push positions and battery (Swift)',
          code:
`/// The completion gets false until window.evWidget exists — keep the fix and retry.
func pushPosition(_ location: CLLocation) {
    // String(format:) is not localized, so decimals always use a dot.
    let js = String(format:
        "(function(){ if (!window.evWidget) return false; window.evWidget.gpsPositionUpdate(" +
        "{ lat: %.7f, lng: %.7f, speed: %.2f, heading: %.1f, accuracy: %.1f, altitude: %.1f, timestamp: %.0f }" +
        "); return true; })()",
        location.coordinate.latitude, location.coordinate.longitude,
        max(location.speed, 0), max(location.course, 0),
        location.horizontalAccuracy, location.altitude,
        location.timestamp.timeIntervalSince1970 * 1000)
    webView.evaluateJavaScript(js) { result, _ in
        if (result as? Bool) != true { /* widget not ready yet: retry in ~300 ms */ }
    }
}

func pushBattery(_ level: Int) {
    webView.evaluateJavaScript(
        "(function(){ if (!window.evWidget) return false; window.evWidget.setBattery(\\(level)); return true; })()")
}

// CLLocationManagerDelegate
func locationManager(_ manager: CLLocationManager, didUpdateLocations locations: [CLLocation]) {
    if let last = locations.last { pushPosition(last) }
}`,
        },
      ];

    case 'flutter':
      return [
        {
          id: 'flutter-bridge', title: 'Push positions and battery (Dart)',
          code:
`/// false until window.evWidget exists — keep the fix and retry.
Future<bool> pushPosition(Position p) async {
  final js = '(function(){ if (!window.evWidget) return false; '
      'window.evWidget.gpsPositionUpdate({ lat: \${p.latitude}, lng: \${p.longitude}, '
      'speed: \${p.speed}, heading: \${p.heading}, accuracy: \${p.accuracy}, '
      'altitude: \${p.altitude}, timestamp: \${p.timestamp.millisecondsSinceEpoch} }); '
      'return true; })()';
  final result = await controller.runJavaScriptReturningResult(js);
  return result == true || result == 1;
}

Future<void> pushBattery(int level) => controller.runJavaScript(
    'window.evWidget && window.evWidget.setBattery(\$level);');

// geolocator: a position stream while the page is visible
final positions = Geolocator.getPositionStream(
  locationSettings: const LocationSettings(accuracy: LocationAccuracy.high),
).listen(pushPosition);`,
        },
      ];
  }
}

